import { escapeHtml, formatDate } from '../../utils/format';
import { isInsufficientStock } from './pickLogic';

// Production sheet for the lines that can't be picked from stock. It's printed from
// a popup window, so the page carries its own (neutral, black-and-white friendly) CSS.

const isFrame = (item) => item.itemId?.toUpperCase().includes('RM-FR');
const isMount = (item) => item.itemId?.toUpperCase().includes('RM-MT');

// Pending lines with insufficient stock, split into frames, mounts and everything else
export const splitManufactureItems = (items) => {
  const toMake = items.filter(item => !item.picked && isInsufficientStock(item));
  return {
    all: toMake,
    frames: toMake.filter(isFrame),
    mounts: toMake.filter(isMount),
    other: toMake.filter(item => !isFrame(item) && !isMount(item)),
  };
};

const rowsHtml = (sectionItems) => sectionItems.map((item, idx) => `
  <tr>
    <td>${idx + 1}</td>
    <td>${escapeHtml(item.name)}</td>
    <td>${Number(item.quantity) - Number(item.warehouseBalance)}</td>
  </tr>
`).join('');

const sectionHtml = (title, sectionItems) => {
  if (sectionItems.length === 0) return '';
  return `
    <div class="section">
      <h2>${title}</h2>
      <table>
        <thead>
          <tr><th>#</th><th>Item</th><th>Qty to Make</th></tr>
        </thead>
        <tbody>${rowsHtml(sectionItems)}</tbody>
      </table>
    </div>
  `;
};

// printType: 'frames' | 'mounts' | 'both'. "Other" lines are always included.
export const buildProductionSheetHtml = (order, items, printType, now = new Date()) => {
  const { frames, mounts, other } = splitManufactureItems(items);
  const showFrames = printType === 'frames' || printType === 'both';
  const showMounts = printType === 'mounts' || printType === 'both';

  const summary = [];
  if (showFrames && frames.length > 0) summary.push(`Frames: ${frames.length}`);
  if (showMounts && mounts.length > 0) summary.push(`Mounts: ${mounts.length}`);
  if (other.length > 0) summary.push(`Other: ${other.length}`);

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Production Sheet - Order #${escapeHtml(order.id)}</title>
      <style>
        @page { size: A4; margin: 20mm; }
        * { box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 20px; color: #111; }
        .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #111; padding-bottom: 20px; }
        .header h1 { margin: 0 0 10px 0; font-size: 28px; letter-spacing: 0.04em; }
        .header .order-info { font-size: 18px; color: #333; }
        .meta { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 14px; color: #333; }
        .section { margin-top: 30px; page-break-inside: avoid; }
        .section h2 { margin: 0 0 15px 0; font-size: 20px; border-bottom: 2px solid #111; padding-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #e5e5e5; color: #111; padding: 12px 16px; text-align: left; font-weight: 600; border-bottom: 2px solid #111; }
        th:last-child { text-align: center; width: 120px; }
        td { padding: 12px 16px; border-bottom: 1px solid #ccc; }
        td:last-child { text-align: center; font-weight: 600; font-size: 18px; }
        tr:nth-child(even) td { background: #f5f5f5; }
        .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #666; }
        @media print {
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          tr { page-break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>PRODUCTION SHEET</h1>
        <div class="order-info">Order #${escapeHtml(order.id)}</div>
      </div>
      <div class="meta">
        <div><strong>Customer:</strong> ${escapeHtml(order.customerName)}</div>
        <div><strong>Dispatch Date:</strong> ${order.dispatchDate ? formatDate(order.dispatchDate) : formatDate(order.createTime)}</div>
      </div>
      <div class="meta">
        <div><strong>Generated:</strong> ${now.toLocaleString()}</div>
        <div><strong>${summary.join(' | ')}</strong></div>
      </div>
      ${showFrames ? sectionHtml('FRAMES', frames) : ''}
      ${showMounts ? sectionHtml('MOUNTS', mounts) : ''}
      ${sectionHtml('OTHER', other)}
      <div class="footer">Generated from Dispatch Pick System</div>
    </body>
    </html>
  `;
};
